import { useEffect, useState } from "react";
import { ScanEye, ShieldEllipsis, X } from "lucide-react";

import {
  Button,
  Dialog,
  Field,
  InlineMessage,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
  Select,
} from "@nexus/ui";
import { isClientProfile, profileLabel, SELECTABLE_PROFILES } from "@/config/uiConfig";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useAccount, type Account } from "@/hooks/useAccount";
import { useTenants } from "@/hooks/useTenants";
import {
  previewLabel,
  usePreview,
  usePreviewControls,
  type NewPreview,
  type PreviewTarget,
} from "@/lib/preview";
import { cn } from "@/lib/utils";
import { useAdminAccounts } from "@/pages/admin/useAdminAccounts";

const ADMIN_PROFILE = "nagra-admin";

/**
 * Administrator tools, top right of the top bar: kept out of the main interface
 * and grouped in one place that can take more tools later. It shows for
 * administrators, and stays while one previews someone else (whose profile the
 * interface then has), so the preview can always be stopped.
 *
 * This is only the gate: it reads the profile and the preview, nothing else, so
 * every other profile and every page without an account pay nothing for it.
 */
export function AdminMenu() {
  const profile = useOptionalAuthorization()?.profile;
  const preview = usePreview();
  if (!preview && profile !== ADMIN_PROFILE) return null;
  return <AdminTools preview={preview} />;
}

function AdminTools({ preview }: { preview: PreviewTarget | null }) {
  const controls = usePreviewControls();
  const [choosing, setChoosing] = useState(false);
  const unsupported = usePreviewGuard();
  const previewing = preview !== null;
  const label = preview ? previewLabel(preview) : "";
  const previewDescription = preview ? `Previewing as ${label} · read only` : undefined;

  const notice = unsupported.shown ? (
    <div role="alert" className="hidden max-w-xs md:block">
      <InlineMessage tone="warning" title="Preview is not available" className="py-1 text-xs">
        The API did not apply it.{" "}
        <button type="button" className="underline" onClick={unsupported.dismiss}>
          Dismiss
        </button>
      </InlineMessage>
    </div>
  ) : null;

  return (
    <div className="flex min-w-0 items-center justify-end gap-2">
      {notice}
      <Menu modal={false}>
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label="Administrator tools"
            aria-description={previewDescription}
            title={previewDescription ?? "Administrator tools"}
            className={cn(
              "relative inline-flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              previewing
                ? "bg-status-warn-bg text-status-warn-text hover:bg-status-warn/20 data-[state=open]:bg-status-warn/20"
                : "bg-action-primary/10 text-action-primary hover:bg-action-primary/15 data-[state=open]:bg-action-primary/15 dark:bg-action-primary/25 dark:text-primary dark:hover:bg-action-primary/35 dark:data-[state=open]:bg-action-primary/35",
            )}
          >
            <ShieldEllipsis className="size-5" strokeWidth={2} aria-hidden />
            {previewing ? (
              <span
                className="absolute right-1 top-1 size-2 rounded-full bg-status-warn ring-2 ring-background"
                aria-hidden
              />
            ) : null}
          </button>
        </MenuTrigger>
        <MenuContent align="end" className="min-w-60 p-2">
          {preview ? (
            <MenuLabel className="max-w-72 px-3 py-2 font-normal">
              <span className="block break-words font-semibold text-primary">{label}</span>
              <span>Preview · Read only</span>
            </MenuLabel>
          ) : null}
          {previewing ? (
            <MenuItem onSelect={controls.stop} className="min-h-10 cursor-pointer gap-3 px-3">
              <X className="h-3.5 w-3.5 text-muted" aria-hidden />
              Stop previewing
            </MenuItem>
          ) : (
            <MenuItem
              onSelect={() => setChoosing(true)}
              className="min-h-10 cursor-pointer gap-3 px-3"
            >
              <ScanEye className="h-3.5 w-3.5 text-muted" aria-hidden />
              Preview as…
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
      {choosing ? (
        <PreviewAsDialog
          onClose={() => setChoosing(false)}
          onStart={(target) => {
            setChoosing(false);
            controls.start(target);
          }}
        />
      ) : null}
    </div>
  );
}

/** Whether the API answered as the previewed identity (see `usePreviewGuard`). */
function previewApplied(preview: PreviewTarget, account: Account): boolean {
  if (preview.kind === "account") return account.account_id === preview.accountId;
  return (
    account.profile === preview.profile &&
    (!preview.tenantId || account.scopes.includes(`tenant:${preview.tenantId}`))
  );
}

/**
 * Checks that the API applied the preview: once a preview has started, the API
 * must answer `/api/account` as the previewed identity. An API without preview
 * support ignores the parameters, and would show the administrator's own data
 * under a preview banner: the preview is then stopped and the reason shown.
 * Only answers received after the preview started count.
 */
function usePreviewGuard() {
  const preview = usePreview();
  const account = useAccount();
  const controls = usePreviewControls();
  const [shown, setShown] = useState(false);
  const data = account.data;
  const answeredAt = account.dataUpdatedAt;

  useEffect(() => {
    if (preview && data && answeredAt >= preview.startedAt && !previewApplied(preview, data)) {
      setShown(true);
      controls.stop();
    }
  }, [preview, data, answeredAt, controls]);

  return { shown, dismiss: () => setShown(false) };
}

type Mode = "role" | "account";

const MODES: readonly { value: Mode; label: string }[] = [
  { value: "role", label: "Role" },
  { value: "account", label: "Account" },
];

/** Two choices side by side. Real radio buttons: the arrow keys and screen readers work as usual. */
function ModeSwitch({ value, onChange }: { value: Mode; onChange: (mode: Mode) => void }) {
  return (
    <fieldset className="grid grid-cols-2 gap-1 rounded-control bg-surface-elevated p-1">
      <legend className="sr-only">What to preview</legend>
      {MODES.map((mode) => (
        <label
          key={mode.value}
          className={cn(
            "cursor-pointer rounded-md px-3 py-1.5 text-center text-sm font-medium transition-colors",
            "has-focus-visible:ring-2 has-focus-visible:ring-focus",
            value === mode.value
              ? "bg-surface text-primary shadow-sm"
              : "text-secondary hover:text-primary",
          )}
        >
          <input
            type="radio"
            name="preview-mode"
            value={mode.value}
            checked={value === mode.value}
            onChange={() => onChange(mode.value)}
            className="sr-only"
          />
          {mode.label}
        </label>
      ))}
    </fieldset>
  );
}

const ROLE_PROFILES = SELECTABLE_PROFILES.filter((profile) => profile !== ADMIN_PROFILE);

function PreviewAsDialog({
  onClose,
  onStart,
}: {
  onClose: () => void;
  onStart: (target: NewPreview) => void;
}) {
  const [mode, setMode] = useState<Mode>("role");
  const [profile, setProfile] = useState<string>(
    ROLE_PROFILES.find(isClientProfile) ?? ROLE_PROFILES[0] ?? "",
  );
  const [tenantId, setTenantId] = useState("");
  const [accountId, setAccountId] = useState("");

  const tenants = useTenants();
  const accounts = useAdminAccounts();
  const me = useAccount().data?.account_id;

  // A client profile reads one tenant: which one is part of what is previewed.
  const needsTenant = isClientProfile(profile);
  const tenantOptions = [...(tenants.data ?? [])]
    .sort(
      (a, b) =>
        (a.customer ?? "").localeCompare(b.customer ?? "") ||
        a.tenant_label.localeCompare(b.tenant_label),
    )
    .map((tenant) => ({
      value: tenant.tenant_id,
      label: tenant.customer ? `${tenant.tenant_label} · ${tenant.customer}` : tenant.tenant_label,
    }));
  const tenant = tenants.data?.find((entry) => entry.tenant_id === tenantId);

  // An administrator sees everything already: only other profiles are worth previewing.
  const candidates = (accounts.data ?? [])
    .filter((account) => account.profile !== ADMIN_PROFILE && account.account_id !== me)
    .sort((a, b) => a.profile.localeCompare(b.profile) || a.email.localeCompare(b.email));
  const account = candidates.find((entry) => entry.account_id === accountId);

  const target: NewPreview | null =
    mode === "account"
      ? account
        ? {
            kind: "account",
            accountId: account.account_id,
            email: account.email,
            profile: account.profile,
          }
        : null
      : !profile || (needsTenant && !tenant)
        ? null
        : {
            kind: "role",
            profile,
            ...(needsTenant && tenant
              ? { tenantId: tenant.tenant_id, tenantLabel: tenant.tenant_label }
              : {}),
          };

  return (
    <Dialog
      open
      title="Preview as"
      description="Read only"
      size="sm"
      className="h-[26rem]"
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        if (target) onStart(target);
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!target}>
            Start preview
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ModeSwitch value={mode} onChange={setMode} />

        {mode === "role" ? (
          <>
            <Field label="Profile">
              <Select
                label="Profile"
                value={profile}
                options={ROLE_PROFILES.map((entry) => ({
                  value: entry,
                  label: profileLabel(entry),
                }))}
                onValueChange={setProfile}
              />
            </Field>
            {needsTenant ? (
              <Field label="Tenant">
                <Select
                  label="Tenant"
                  value={tenantId}
                  options={tenantOptions}
                  onValueChange={setTenantId}
                  placeholder={tenants.isPending ? "Loading tenants…" : "Choose a tenant"}
                  disabled={tenants.isPending || tenantOptions.length === 0}
                />
              </Field>
            ) : null}
          </>
        ) : (
          <>
            {accounts.isError ? (
              <InlineMessage tone="error" title="Accounts could not be loaded">
                Try again later.
              </InlineMessage>
            ) : null}
            <Field label="Account">
              <Select
                label="Account"
                value={accountId}
                options={candidates.map((entry) => ({
                  value: entry.account_id,
                  label: `${entry.email} · ${profileLabel(entry.profile)}`,
                }))}
                onValueChange={setAccountId}
                placeholder={accounts.isPending ? "Loading accounts…" : "Choose an account"}
                disabled={accounts.isPending || candidates.length === 0}
              />
            </Field>
            {accounts.isSuccess && candidates.length === 0 ? (
              <p className="text-sm text-muted">No accounts available.</p>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}
